import { useContextPath } from '@cube3/state/src/redux/components/Hooks/useContextPath';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { mergeStatuses } from '@cube3/state/src/redux/utils/mergeStatuses';
import { getData } from '../utils/getRelationshipData';
import { useProjectCheck } from './useProjectCheck';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { useMemo } from 'react';
import { Activity } from '@cube3/common/model/schema/resources/activities';

/** Get the parent folder of the shared node */
export const useShareContext = (shareRID) => {
  const { resource: share } = useResource__ALPHA({
    resourceType: shareRID?.type === 'share' ? 'share' : undefined,
    resourceId: shareRID?.type === 'share' ? shareRID?.id : undefined
  });

  const node = getData(share, 'node');
  const ancestors = useResourceList__ALPHA({
    resourceType: node ? 'content-tree-node' : undefined,
    resourceId: node?.id,
    edgeType: node ? 'folder' : undefined,
    edgeLabel: 'ancestors'
  });

  return useMemo(() => {
    if (!ancestors.resources) return null;
    const parent = ancestors.resources?.reverse()[0];

    return {
      ...parent,
      type: ancestors.resources.length === 1 ? 'project' : 'folder'
    };
  }, [ancestors.resources]);
};

/** Get the parent folder/project of the object that was modified */
export const useActivityContextWithPath = (activity: Activity) => {
  const contextRIDS = activity.relationships.related_resources;

  const folder = contextRIDS?.find((c) => c.type === 'folder');
  const project = contextRIDS?.find((c) => c.type === 'project');
  const projectResouce = useResource__ALPHA<'project'>({
    resourceId: project?.id,
    resourceType: project ? 'project' : undefined
  });
  const folderResource = useResource__ALPHA<'folder'>({
    resourceId: folder?.id,
    resourceType: folder ? 'folder' : undefined
  });

  const isProjectSelf = useProjectCheck(folder?.id, project?.id);

  // for `ShareCreated` activity, we want to show the parent folder of the shared node
  const subject = activity.relationships.subject;
  const shareContext = useShareContext(
    subject?.type === 'share' ? subject : null
  );

  const [ancestorPath] = useContextPath({
    resourceId: shareContext ? shareContext.id : folder?.id
    //edgeResourceType: folder?.type,
    // edgeResourceId: folder?.id
  });
  //
  const loading = folderResource.loading || projectResouce.loading;
  const status = mergeStatuses([folderResource.status], true);

  // for `ContractCreated`, there's only `project` returned in `related_resources`
  const hasProjectOnly = project && !folder;
  return {
    type: shareContext
      ? shareContext.type
      : isProjectSelf || hasProjectOnly
      ? 'project'
      : 'folder',
    name:
      shareContext?.display_name ||
      (hasProjectOnly
        ? projectResouce.resource?.display_name
        : folderResource.resource?.display_name),
    pathname: ancestorPath,
    resource: shareContext || folderResource.resource,
    loading,
    status
  };
};
/** Get parent folder / download type / export-target / share comment / share approvals from related_resources
 * - For `Export` activities, return `export-target` to display the correct icon.
 * - For "downloaded" activities, return "render-preset" to display as download type.
 * - If the activity (comment, approve, view, download, ...) happened in a share/review link,
 *    - return a `share` to display as context,
 *    - return a `comment`/`approval` to display in the dataSection
 */
export const useActivityContext = (contextRIDS) => {
  const ctx = contextRIDS?.find((c) =>
    [
      'render-preset',
      'export-target',
      'folder',
      'comment',
      'approval'
    ].includes(c.type)
  );

  const shareRID = contextRIDS?.find((c) => c.type === 'share');

  const share = useResource__ALPHA({
    resourceType: shareRID ? 'share' : undefined,
    resourceId: shareRID?.id
  });

  const res = useResource__ALPHA({
    resourceId: ctx?.id,
    resourceType: ctx?.type,
    // NOTE: `export-target` from `included` is missing neccessary attributes
    strategy: ctx?.type === 'export-target' ? 'fetch-on-mount' : 'prefer-cache'
  });

  return {
    name: res.resource?.display_name,
    resource: res.resource,
    loading: res.loading || (shareRID && share.loading),
    share: share.resource
  };
};
